import nudgeAngelDispatcher from '../action';
import {
  NUGDE_ACTION,
  NUGDE_FALLBACK_ACTION,
  NUGDE_TYPE,
  TYPE_MODAL,
} from '../constants';
import { CloseIcon, RadioActiveIcon, RadioInactiveIcon } from '@/assets/svg';
import {
  TINY_API_KEY,
  TINY_INIT_CONFIGURATION,
} from '@/constants/editorConfig';
import { DOMAIN_PATTERN } from '@/helpers/validationHelpers/validationSchema';
import { CustomFormHelperText } from '@/module/all-screenings/component/ViewPatientRegistration/ModalInputServices';
import CustomLoadingButton from '@/new-components/CustomLoadingButton';
import CustomSelectContainer from '@/new-components/CustomSelectOld/CustomSelectContainer';
import {
  Box,
  Grid,
  IconButton,
  Paper,
  Typography,
  makeStyles,
  withStyles,
  TextField,
  CircularProgress,
  FormControlLabel,
  RadioGroup,
  Radio,
} from '@material-ui/core';
import { InfoOutlined } from '@material-ui/icons';
import { Editor } from '@tinymce/tinymce-react';
import { Divider } from 'antd';
import { cloneDeep, isBoolean, isEmpty, isEqual } from 'lodash';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import NumberFormat from 'react-number-format';

const CustomMainLabel = withStyles((theme) => ({
  root: {
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 16,
    color: '#191919',
  },
}))(Typography);

const CustomInputLabel = withStyles((theme) => ({
  root: {
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 4,
    color: '#666666',
  },
}))(Typography);

const CustomTextField = withStyles({
  root: {
    fontSize: 20,
    width: '100%',

    '& .MuiOutlinedInput-root': {
      height: 48,
      borderRadius: 8,
    },
    '& .MuiOutlinedInput-input': {
      marginRight: (props) => (props.hasClear ? 0 : 30),
      padding: '8px 5px',
      fontSize: 16,
    },

    '& .Mui-disabled': {
      '&.MuiOutlinedInput-root': {
        backgroundColor: '#f6f6f6',
      },
    },
    '& .MuiAutocomplete-inputRoot[class*="MuiOutlinedInput-root"]': {
      padding: 0,
    },
    '& .MuiInputBase-multiline': {
      height: 'auto',
      paddingLeft: 0,

      '& .MuiOutlinedInput-input': {
        marginRight: 8,
      },
    },
    '& .MuiFormHelperText-contained': {
      display: 'flex',
      alignItems: 'center',
      fontSize: 12,
      marginLeft: 0,
      '& .MuiSvgIcon-root': {
        width: 18,
        height: 18,
        marginRight: 4,
      },
    },
  },
})(TextField);

const CustomPaper = withStyles((theme) => ({
  root: {
    '& .MuiIconButton-root:hover': {
      backgroundColor: 'transparent',
    },

    '& .MuiAutocomplete-option': {
      padding: 8,
      borderRadius: 8,
      margin: '2px 8px',
      '&:first-child': {
        marginTop: 8,
      },
      '&:last-child': {
        marginBottom: 8,
      },
      '&.MuiAutocomplete-option[aria-selected="true"]': {
        color: theme.palette.common.white,
        backgroundColor: theme.palette.primary.main,
      },
    },
  },
}))(Paper);

const titleStyles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(2),
  },
  title: {
    fontWeight: 600,
    fontSize: 18,
    color: '#333',
  },
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: 14,
    color: theme.palette.grey[500],
  },
});

const useStyles = makeStyles((theme) => ({
  mainWrapper: {
    height: 'calc(100vh - 140px)',
    overflow: 'auto',
  },
  stepsControlWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: theme.spacing(1),
  },
}));

const CustomDrawerTitle = withStyles(titleStyles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <Box className={classes.root} {...other} borderBottom={'1px solid #E0E0E0'}>
      <Typography variant="h6" className={classes.title}>
        {children}
      </Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </Box>
  );
});

const ModalAddOrEditAngelNudge = ({
  loading,
  onClose,
  onSubmit,
  typeModal,
  selectedItem,
  eventTriggers,
}) => {
  const classes = useStyles();
  const [errors, setErrors] = useState({ notificationUrl: [] });
  const [itemsToShow, setItemsToShow] = useState(5); // Number of items to show initially
  const [isLoadingData, setIsLoadingData] = useState(false);

  const [formValue, setFormValue] = useState({
    totalFollowUpEvent: 0,
    fallbackAction: '',
    finalFallbackAction: '',
    nudgeAngels: [
      {
        reminderOrder: null,
        triggerAfterHours: 0,
        triggerTime: '',
        isActive: false,
        emailSubject: '',
        emailContent: '',
        notificationTitle: '',
        notificationContent: '',
        notificationUrl: '',
      },
    ],
    nudgeType: '',
    eventTrigger: '',
    name: '',
    sqlQuery: '',
    description: '',
    triggerTime: '',
    code: '',
  });

  const handleChangeForm = (key) => (e, newValue) => {
    let value = '';
    switch (key) {
      case 'nudgeType':
        value = e.target.value;

        if (value === 'Rules') {
          setFormValue({
            ...formValue,
            nudgeType: value,
            eventTrigger: '',
          });
        } else {
          setFormValue({
            ...formValue,
            nudgeType: value,
            triggerTime: '',
            sqlQuery: '',
          });
        }
        return;
      case 'fallbackAction':
        value = e.target.value;
        if (e.target.value === 'SendAgain') {
          setFormValue({
            ...formValue,
            fallbackAction: e.target.value,
          });
        } else {
          let newNudgeAngels = [
            {
              ...formValue?.nudgeAngels[0],
            },
          ];
          newNudgeAngels[0].reminderOrder = null;

          setFormValue({
            ...formValue,
            finalFallbackAction: '',
            fallbackAction: e.target.value,
            nudgeAngels: newNudgeAngels,
            totalFollowUpEvent: 0,
          });
        }
        return;

      case 'totalFollowUpEvent':
        value = e.target.value;
        let nudgeAngels = [
          {
            ...formValue?.nudgeAngels[0],
          },
        ];
        nudgeAngels[0].reminderOrder = 0;

        if (e.target.value > 0) {
          let newNudgeAngels = [];

          for (let i = 0; i < e.target.value; i++) {
            newNudgeAngels.push({
              reminderOrder: i + 1,
              triggerAfterHours: 0,
              triggerTime: '',
              isActive: false,
              emailSubject: '',
              emailContent: '',
              notificationTitle: '',
              notificationContent: '',
              notificationUrl: '',
            });
          }
          nudgeAngels = [...nudgeAngels, ...newNudgeAngels];
        } else {
          let newNudgeAngels = [
            {
              ...formValue?.nudgeAngels[0],
            },
          ];
          // newNudgeAngels[0].reminderOrder = null;
          nudgeAngels = newNudgeAngels;
        }
        setFormValue({
          ...formValue,
          [key]: value,
          nudgeAngels: nudgeAngels,
        });
        return;

      case 'eventTrigger':
        value = e.target.value;
        setFormValue({
          ...formValue,
          eventTrigger: eventTriggers.find((it) => it.id === value)
            ?.eventTrigger,
        });
        return;
      default:
        value = e.target.value;
        break;
    }

    setFormValue({ ...formValue, [key]: value });
  };

  const isValidDomains = useMemo(() => {
    let errorNotificationUrl = [];

    if (!isEmpty(formValue?.nudgeAngels)) {
      const notificationUrl = formValue?.nudgeAngels.map(
        (item) => item.notificationUrl
      );
      errorNotificationUrl = notificationUrl.map((item) => {
        if (!item) return; // Skip validation if notificationUrl is empty
        DOMAIN_PATTERN.lastIndex = 0; // Reset the regex
        return !DOMAIN_PATTERN.test(item);
      });

      if (errorNotificationUrl.some((item) => item)) {
        return errorNotificationUrl;
      } else {
        return [];
      }
    }
    return errorNotificationUrl;
  }, [formValue.nudgeAngels]);

  const handleSubmit = () => {
    let newFormValue = {
      ...formValue,
      totalFollowUpEvent: Number(formValue.totalFollowUpEvent),
      nudgeAngels: formValue?.nudgeAngels.map((item) => ({
        ...item,
        triggerAfterHours: Number(item.triggerAfterHours),
      })),
    };
    if (isValidDomains.length > 0) {
      setErrors({
        notificationUrl: isValidDomains,
      });
    } else {
      setErrors({
        notificationUrl: [],
      });
      onSubmit(typeModal, newFormValue);
    }
  };

  const isEmptyParams = useMemo(() => {
    const filterKeys = ['nudgeType', 'name', 'fallbackAction'];

    if (formValue.nudgeType === 'Rules') {
      filterKeys.push('triggerTime', 'sqlQuery');
    } else {
      filterKeys.push('eventTrigger');
    }

    if (formValue.fallbackAction === 'SendAgain') {
      filterKeys.push('totalFollowUpEvent', 'finalFallbackAction');
    }

    const isCheckEmpty = formValue.nudgeAngels.filter(
      (item) =>
        isEmpty(item.notificationContent) || isEmpty(item.notificationTitle)
    );

    return (
      !isEmpty(isCheckEmpty) ||
      filterKeys.some((key) =>
        isBoolean(formValue[key]) ? !formValue[key] : isEmpty(formValue[key])
      )
    );
  }, [formValue]);

  const getNudgeDetail = async () => {
    setIsLoadingData(true);
    nudgeAngelDispatcher.getDetailNudgeAngel(selectedItem?.id, (result) => {
      const newFormValue = {
        id: selectedItem?.id,
        totalFollowUpEvent: String(result?.totalFollowUpEvent),
        fallbackAction: result?.fallbackAction,
        finalFallbackAction: result?.finalFallbackAction,
        nudgeAngels: result?.followUpNudges.map((item, i) => ({
          id: item.id,
          reminderOrder: item.reminderOrder,
          triggerAfterHours: String(item.triggerAfterHours),
          triggerTime: item.triggerTime,
          isActive: item.isActive,
          emailSubject: item.emailSubject,
          emailContent: item.emailContent,
          notificationTitle: item.pushTitle,
          notificationContent: item.pushContent,
          notificationUrl: item.pushURL,
        })),
        nudgeType: result?.nudgeType,
        eventTrigger: selectedItem?.eventTrigger,
        name: result?.name,
        sqlQuery: result?.sqlQuery,
        triggerTime: result?.triggerTime,
      };

      setIsLoadingData(false);
      setFormValue(newFormValue);
    });
  };

  useEffect(() => {
    if (!selectedItem) return;
    getNudgeDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMoreData = () => {
    // Add 5 more items when user scrolls
    setItemsToShow((prevItems) => prevItems + 5);
  };

  return (
    <Paper square elevation={0}>
      <CustomDrawerTitle onClose={onClose}>
        {typeModal === TYPE_MODAL.Edit
          ? 'Edit Parent Nudge'
          : 'Create New Parent Nudge'}
      </CustomDrawerTitle>
      {isLoadingData ? (
        <Box textAlign="center" height={'100vh'}>
          <CircularProgress />
        </Box>
      ) : (
        <InfiniteScroll
          dataLength={itemsToShow}
          next={fetchMoreData}
          hasMore={itemsToShow < formValue?.nudgeAngels?.slice(1)?.length}
          scrollableTarget="scrollableDiv"
        >
          <main className={classes.mainWrapper} id="scrollableDiv">
            <Box p={3}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <CustomMainLabel>Create Parent Nudge Rule</CustomMainLabel>
                </Grid>
                <Grid item xs={6}>
                  <CustomInputLabel>Nudge Type</CustomInputLabel>

                  <CustomSelectContainer
                    data={NUGDE_TYPE || []}
                    height="48px"
                    fontSize="16px"
                    isMutiple={false}
                    hideClear
                    colorInput="#000000de"
                    placeholder="Select Nudge Type"
                    renderValue={
                      formValue.nudgeType
                        ? NUGDE_TYPE.find(
                            (it) => it.key === formValue.nudgeType
                          )?.value
                        : 'Select Nudge Type'
                    }
                    filterValue={formValue.nudgeType || ''}
                    onChange={handleChangeForm('nudgeType')}
                  />
                </Grid>
                {!isEmpty(formValue.nudgeType) && (
                  <>
                    <Grid item xs={6}>
                      <CustomInputLabel>Nudge Name</CustomInputLabel>
                      <CustomTextField
                        fullWidth
                        value={formValue.name || ''}
                        onChange={handleChangeForm('name')}
                        variant="outlined"
                        placeholder="Enter Nudge Name"
                      />
                    </Grid>
                    {formValue.nudgeType === 'Event' ? (
                      <Grid item xs={12}>
                        <CustomInputLabel>Event Trigger</CustomInputLabel>

                        <CustomSelectContainer
                          data={eventTriggers || []}
                          height="48px"
                          fontSize="16px"
                          isMutiple={false}
                          hideClear
                          colorInput="#000000de"
                          placeholder="Select Event Trigger"
                          renderValue={
                            formValue.eventTrigger
                              ? eventTriggers.find(
                                  (it) =>
                                    it.eventTrigger === formValue.eventTrigger
                                )?.name
                              : 'Select Event Trigger'
                          }
                          filterValue={formValue.eventTrigger || ''}
                          onChange={handleChangeForm('eventTrigger')}
                        />
                      </Grid>
                    ) : (
                      <>
                        <Grid item xs={12}>
                          <CustomInputLabel>
                            SQL Query to Fetch Patients
                          </CustomInputLabel>

                          <CustomTextField
                            rows={3}
                            multiline
                            fullWidth
                            variant="outlined"
                            placeholder={
                              "SELECT * FROM patients WHERE condition = 'Diabetes'"
                            }
                            value={formValue.sqlQuery || ''}
                            onChange={handleChangeForm('sqlQuery')}
                            helperText={
                              <>
                                <InfoOutlined /> Only SELECT statements are
                                allowed
                              </>
                            }
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <CustomInputLabel>Trigger Time</CustomInputLabel>
                          <CustomTextField
                            fullWidth
                            variant="outlined"
                            placeholder="Enter Trigger Time in hours"
                            value={formValue.triggerTime || ''}
                            onChange={handleChangeForm('triggerTime')}
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12}>
                      <Divider light style={{ margin: 8 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <CustomMainLabel>Email Nudge</CustomMainLabel>
                    </Grid>
                    <Grid item xs={6}>
                      <CustomInputLabel>Email Subject</CustomInputLabel>
                      <CustomTextField
                        fullWidth
                        value={formValue?.nudgeAngels[0]?.emailSubject || ''}
                        onChange={(e) => {
                          const newNudgeAngels = [...formValue?.nudgeAngels];
                          newNudgeAngels[0].emailSubject = e.target.value;
                          setFormValue({
                            ...formValue,
                            nudgeAngels: newNudgeAngels,
                          });
                        }}
                        variant="outlined"
                        placeholder="Enter Email Subject"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <CustomInputLabel>Email Content</CustomInputLabel>
                      <Editor
                        apiKey={TINY_API_KEY}
                        init={TINY_INIT_CONFIGURATION}
                        value={formValue?.nudgeAngels[0]?.emailContent || ''}
                        onEditorChange={(e) => {
                          const newNudgeAngels = [...formValue?.nudgeAngels];
                          newNudgeAngels[0].emailContent = e;
                          setFormValue({
                            ...formValue,
                            nudgeAngels: newNudgeAngels,
                          });
                        }}
                        placeholder="Email Content"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider light style={{ margin: 8 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <CustomMainLabel>Push Notification</CustomMainLabel>
                    </Grid>
                    <Grid item xs={6}>
                      <CustomInputLabel>Push Title</CustomInputLabel>
                      <CustomTextField
                        fullWidth
                        value={
                          formValue?.nudgeAngels[0]?.notificationTitle || ''
                        }
                        onChange={(e) => {
                          const newNudgeAngels = [...formValue?.nudgeAngels];
                          newNudgeAngels[0].notificationTitle = e.target.value;
                          setFormValue({
                            ...formValue,
                            nudgeAngels: newNudgeAngels,
                          });
                        }}
                        variant="outlined"
                        placeholder="Enter Push Title"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <CustomInputLabel>Push Content</CustomInputLabel>
                      <CustomTextField
                        rows={3}
                        multiline
                        fullWidth
                        variant="outlined"
                        placeholder={'Enter Push Content'}
                        value={
                          formValue?.nudgeAngels[0]?.notificationContent || ''
                        }
                        onChange={(e) => {
                          const newNudgeAngels = [...formValue?.nudgeAngels];
                          newNudgeAngels[0].notificationContent =
                            e.target.value;
                          setFormValue({
                            ...formValue,
                            nudgeAngels: newNudgeAngels,
                          });
                        }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <CustomInputLabel>Push URL</CustomInputLabel>
                      <CustomTextField
                        fullWidth
                        value={formValue?.nudgeAngels[0]?.notificationUrl || ''}
                        onChange={(e) => {
                          const newNudgeAngels = [...formValue?.nudgeAngels];
                          newNudgeAngels[0].notificationUrl = e.target.value;
                          setFormValue({
                            ...formValue,
                            nudgeAngels: newNudgeAngels,
                          });
                        }}
                        variant="outlined"
                        placeholder="Enter Push URL"
                      />
                      {!isEmpty(errors.notificationUrl) &&
                        errors.notificationUrl[0] && (
                          <CustomFormHelperText error>
                            This url is invalid! Please be sure to include
                            http:// or https://
                          </CustomFormHelperText>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                      <Divider light style={{ margin: 8 }} />
                    </Grid>
                    <Grid item xs={6}>
                      <CustomInputLabel>
                        If Nudge Not Acknowledged
                      </CustomInputLabel>

                      <CustomSelectContainer
                        data={NUGDE_ACTION || []}
                        height="48px"
                        fontSize="16px"
                        isMutiple={false}
                        hideClear
                        colorInput="#000000de"
                        placeholder="Select an action"
                        renderValue={
                          formValue.fallbackAction
                            ? NUGDE_ACTION.find(
                                (it) => it.key === formValue.fallbackAction
                              )?.value
                            : 'Select an action'
                        }
                        filterValue={formValue.fallbackAction || ''}
                        onChange={handleChangeForm('fallbackAction')}
                      />
                    </Grid>
                    {formValue.fallbackAction === 'SendAgain' && (
                      <>
                        <Grid item xs={6}>
                          <CustomInputLabel>
                            Number of Follow-up Nudges
                          </CustomInputLabel>

                          <NumberFormat
                            fullWidth
                            customInput={CustomTextField}
                            variant="outlined"
                            placeholder="Enter Number of Follow-up Nudges"
                            value={formValue.totalFollowUpEvent || ''}
                            onChange={handleChangeForm('totalFollowUpEvent')}
                            allowNegative={false}
                            onInput={(e) => {
                              if (e.target.value === '0') {
                                e.target.value = '';
                              }
                            }}
                          />
                        </Grid>

                        {Number(formValue.totalFollowUpEvent) > 0 && (
                          <>
                            <Grid item xs={12}>
                              <Divider light style={{ margin: 8 }} />
                            </Grid>
                            <Grid item xs={12}>
                              <CustomMainLabel>
                                List of Child Nudges
                              </CustomMainLabel>
                            </Grid>
                            {!isEmpty(formValue?.nudgeAngels) && (
                              <Grid
                                item
                                xs={12}
                                id="scrollableDiv"
                                className={classes.scrollableDiv}
                              >
                                {formValue?.nudgeAngels
                                  .slice(1)
                                  .slice(0, itemsToShow)
                                  .map((item, index) => (
                                    <Grid item xs={12} key={index}>
                                      <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                          <CustomInputLabel>
                                            Nudge {item.reminderOrder + 1} (Send
                                            after Nudge {item.reminderOrder})
                                          </CustomInputLabel>
                                        </Grid>
                                        <Grid item xs={12}>
                                          <CustomInputLabel>
                                            Stastus
                                          </CustomInputLabel>

                                          <RadioGroup
                                            name="isActive"
                                            onChange={(e) => {
                                              const newNudgeAngels = [
                                                ...formValue?.nudgeAngels,
                                              ];
                                              newNudgeAngels[
                                                item.reminderOrder
                                              ].isActive =
                                                e.target.value === 'active'
                                                  ? true
                                                  : false;
                                              setFormValue({
                                                ...formValue,
                                                nudgeAngels: newNudgeAngels,
                                              });
                                            }}
                                            value={
                                              item.isActive
                                                ? 'active'
                                                : 'inactive'
                                            }
                                            style={{ flexDirection: 'row' }}
                                          >
                                            <FormControlLabel
                                              value={'active'}
                                              control={
                                                <Radio
                                                  checked={isEqual(
                                                    item.isActive,
                                                    true
                                                  )}
                                                  color="primary"
                                                  icon={<RadioInactiveIcon />}
                                                  checkedIcon={
                                                    <RadioActiveIcon />
                                                  }
                                                />
                                              }
                                              label="Activate"
                                            />
                                            <FormControlLabel
                                              value={'inactive'}
                                              control={
                                                <Radio
                                                  checked={isEqual(
                                                    item.isActive,
                                                    false
                                                  )}
                                                  color="primary"
                                                  icon={<RadioInactiveIcon />}
                                                  checkedIcon={
                                                    <RadioActiveIcon />
                                                  }
                                                />
                                              }
                                              label="Deactivate"
                                            />
                                          </RadioGroup>
                                        </Grid>
                                        <Grid item xs={6}>
                                          <CustomInputLabel>
                                            Trigger Time After Previous Nudge
                                          </CustomInputLabel>
                                          <NumberFormat
                                            fullWidth
                                            customInput={CustomTextField}
                                            variant="outlined"
                                            placeholder={`e.g. 24 hours after Nudge ${
                                              item.reminderOrder + 1
                                            }`}
                                            value={item.triggerAfterHours || ''}
                                            onChange={(e) => {
                                              const newNudgeAngels = [
                                                ...formValue?.nudgeAngels,
                                              ];
                                              newNudgeAngels[
                                                item.reminderOrder
                                              ].triggerAfterHours =
                                                e.target.value;

                                              setFormValue({
                                                ...formValue,
                                                nudgeAngels: newNudgeAngels,
                                              });
                                            }}
                                            onInput={(e) => {
                                              if (e.target.value === '0') {
                                                e.target.value = '';
                                              }
                                            }}
                                          />
                                        </Grid>
                                        <Grid item xs={6}>
                                          <CustomInputLabel>
                                            Email Subject
                                          </CustomInputLabel>
                                          <CustomTextField
                                            fullWidth
                                            value={item.emailSubject || ''}
                                            onChange={(e) => {
                                              const newNudgeAngels = [
                                                ...formValue?.nudgeAngels,
                                              ];
                                              newNudgeAngels[
                                                item.reminderOrder
                                              ].emailSubject = e.target.value;
                                              setFormValue({
                                                ...formValue,
                                                nudgeAngels: newNudgeAngels,
                                              });
                                            }}
                                            variant="outlined"
                                            placeholder={`Enter email subject for Nudge ${
                                              item.reminderOrder + 1
                                            }`}
                                          />
                                        </Grid>
                                        <Grid item xs={12}>
                                          <CustomInputLabel>
                                            Email Content
                                          </CustomInputLabel>

                                          <Editor
                                            apiKey={TINY_API_KEY}
                                            init={TINY_INIT_CONFIGURATION}
                                            value={item.emailContent || ''}
                                            onEditorChange={(e) => {
                                              const newNudgeAngels = [
                                                ...formValue?.nudgeAngels,
                                              ];
                                              newNudgeAngels[
                                                item.reminderOrder
                                              ].emailContent = e;
                                              setFormValue({
                                                ...formValue,
                                                nudgeAngels: newNudgeAngels,
                                              });
                                            }}
                                            placeholder="Email Content"
                                          />
                                        </Grid>
                                        <Grid item xs={6}>
                                          <CustomInputLabel>
                                            Push Title
                                          </CustomInputLabel>
                                          <CustomTextField
                                            fullWidth
                                            value={item.notificationTitle || ''}
                                            onChange={(e) => {
                                              const newNudgeAngels = [
                                                ...formValue?.nudgeAngels,
                                              ];
                                              newNudgeAngels[
                                                item.reminderOrder
                                              ].notificationTitle =
                                                e.target.value;
                                              setFormValue({
                                                ...formValue,
                                                nudgeAngels: newNudgeAngels,
                                              });
                                            }}
                                            variant="outlined"
                                            placeholder={`Enter push title for Nudge ${
                                              item.reminderOrder + 1
                                            }`}
                                          />
                                        </Grid>
                                        <Grid item xs={12}>
                                          <CustomInputLabel>
                                            Push Content
                                          </CustomInputLabel>

                                          <CustomTextField
                                            rows={3}
                                            multiline
                                            fullWidth
                                            variant="outlined"
                                            placeholder={`Enter push content for Nudge ${
                                              item.reminderOrder + 1
                                            }`}
                                            value={
                                              item.notificationContent || ''
                                            }
                                            onChange={(e) => {
                                              const newNudgeAngels = [
                                                ...formValue?.nudgeAngels,
                                              ];
                                              newNudgeAngels[
                                                item.reminderOrder
                                              ].notificationContent =
                                                e.target.value;
                                              setFormValue({
                                                ...formValue,
                                                nudgeAngels: newNudgeAngels,
                                              });
                                            }}
                                          />
                                        </Grid>
                                        <Grid item xs={6}>
                                          <CustomInputLabel>
                                            Push URL
                                          </CustomInputLabel>
                                          <CustomTextField
                                            fullWidth
                                            value={item.notificationUrl || ''}
                                            onChange={(e) => {
                                              const newNudgeAngels = [
                                                ...formValue?.nudgeAngels,
                                              ];
                                              newNudgeAngels[
                                                item.reminderOrder
                                              ].notificationUrl =
                                                e.target.value;
                                              setFormValue({
                                                ...formValue,
                                                nudgeAngels: newNudgeAngels,
                                              });
                                            }}
                                            variant="outlined"
                                            placeholder={`Enter push URL for Nudge ${
                                              item.reminderOrder + 1
                                            }`}
                                          />
                                          {!isEmpty(errors?.notificationUrl) &&
                                            errors?.notificationUrl?.slice(1)[
                                              index
                                            ] && (
                                              <CustomFormHelperText error>
                                                This url is invalid! Please be
                                                sure to include http:// or
                                                https://
                                              </CustomFormHelperText>
                                            )}
                                        </Grid>
                                        <Grid
                                          item
                                          xs={12}
                                          style={{
                                            display:
                                              index ===
                                              formValue?.nudgeAngels.slice(1)
                                                .length -
                                                1
                                                ? 'none'
                                                : 'block',
                                          }}
                                        >
                                          <Divider
                                            light
                                            style={{ margin: 8 }}
                                          />
                                        </Grid>
                                      </Grid>
                                    </Grid>
                                  ))}
                              </Grid>
                            )}
                          </>
                        )}

                        <Grid item xs={12}>
                          <Divider light style={{ margin: 8 }} />
                        </Grid>
                        <Grid item xs={6}>
                          <CustomInputLabel>
                            After All Retries, If Still Not Acknowledged
                          </CustomInputLabel>

                          <CustomSelectContainer
                            data={NUGDE_FALLBACK_ACTION || []}
                            height="48px"
                            fontSize="16px"
                            isMutiple={false}
                            hideClear
                            colorInput="#000000de"
                            placeholder="Select an action"
                            renderValue={
                              formValue.finalFallbackAction
                                ? NUGDE_FALLBACK_ACTION.find(
                                    (it) =>
                                      it.key === formValue.finalFallbackAction
                                  )?.value
                                : 'Select an action'
                            }
                            filterValue={formValue.finalFallbackAction || ''}
                            onChange={handleChangeForm('finalFallbackAction')}
                          />
                        </Grid>
                      </>
                    )}
                  </>
                )}
              </Grid>
            </Box>
          </main>
        </InfiniteScroll>
      )}
      <Box
        display="flex"
        justifyContent="flex-end"
        py={1.75}
        px={1.25}
        gridGap={16}
        borderTop={'1px solid #E0E0E0'}
      >
        <CustomLoadingButton
          variant="outlined"
          color="primary"
          onClick={onClose}
        >
          Cancel
        </CustomLoadingButton>

        <CustomLoadingButton
          variant="contained"
          color="primary"
          loading={loading}
          disabled={isEmptyParams || loading}
          onClick={handleSubmit}
        >
          {typeModal === TYPE_MODAL.Create ? 'Save' : 'Update'}
        </CustomLoadingButton>
      </Box>
    </Paper>
  );
};

export default ModalAddOrEditAngelNudge;
